/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useMemo, useState } from 'react'

import { useDashboard } from '@/hooks'
import type { HistoryGraphSettings } from '@/types/dashboard-types'

import type { StatusSection } from './statusRows'

/**
 * The Temperature Graph layout: whether the status bars sit on the graph or under it, and
 * which sections under it are collapsed (all expanded by default). Stored in the profile so every Temperature Graph
 * opens the way the user left the last one. Outside the dashboard provider (no profile to
 * save to) it falls back to local state.
 */
export const useHistoryGraphSettings = () => {
  const { configuration, saveHistoryGraph } = useDashboard()
  const [local, setLocal] = useState<HistoryGraphSettings>({})
  const settings = useMemo(
    () => (saveHistoryGraph ? (configuration?.historyGraph ?? {}) : local),
    [saveHistoryGraph, configuration?.historyGraph, local]
  )

  const update = useCallback(
    (patch: HistoryGraphSettings) => {
      const next = { ...settings, ...patch }
      if (saveHistoryGraph) {
        saveHistoryGraph(next)
      } else {
        setLocal(next)
      }
    },
    [settings, saveHistoryGraph]
  )

  const collapsedSections = useMemo(() => new Set(settings.collapsedSections ?? []), [settings.collapsedSections])

  const setStatusBarsBelow = useCallback((below: boolean) => update({ statusBarsBelow: below }), [update])

  const toggleSection = useCallback(
    (section: StatusSection) => {
      const next = new Set(collapsedSections)
      if (next.has(section)) {
        next.delete(section)
      } else {
        next.add(section)
      }
      update({ collapsedSections: [...next] })
    },
    [collapsedSections, update]
  )

  return {
    statusBarsBelow: settings.statusBarsBelow ?? false,
    collapsedSections,
    setStatusBarsBelow,
    toggleSection,
  }
}
